import { createLiveSession, sendLiveChunk, stopLiveSession } from '../api.js';
import { blobToBase64 } from './LiveRecorder.js';

export class LiveSessionClient {
  constructor({ onStatus, onTranscript, onRequirements, onDeferredDecisions, onLanguage, onChunkStatus, onEngine, onWarnings } = {}) {
    this.onStatus = onStatus;
    this.onTranscript = onTranscript;
    this.onRequirements = onRequirements;
    this.onDeferredDecisions = onDeferredDecisions;
    this.onLanguage = onLanguage;
    this.onChunkStatus = onChunkStatus;
    this.onEngine = onEngine;
    this.onWarnings = onWarnings;
    this.session = null;
    this.queue = Promise.resolve();
    this.requirements = [];
    this.userStories = [];
    this.deferredDecisions = [];
    this.language = null;
    this.transcriptionEngine = null;
    this.extractionWarnings = [];
  }

  async createSession(payload = {}) {
    this.#notifyStatus('Session wird initialisiert…');
    const session = await createLiveSession(payload);
    this.session = session;
    if (session?.transcriptionEngine) {
      this.transcriptionEngine = session.transcriptionEngine;
    }
    this.#handleServerUpdate(session);
    this.#notifyStatus('Session aktiv');
    return session;
  }

  getSession() {
    return this.session;
  }

  getRequirements() {
    return this.requirements.slice();
  }

  getDeferredDecisions() {
    return this.deferredDecisions.slice();
  }

  async sendChunk({ blob, sequence, durationMs, mimeType: chunkMimeType, language: chunkLanguage }) {
    if (!this.session) {
      throw new Error('Keine aktive Session');
    }
    const { sessionId } = this.session;
    const mimeType = chunkMimeType || blob.type || 'audio/webm';
    const chunk = await blobToBase64(blob);
    const resolvedLanguage = chunkLanguage || this.session?.language || this.language || null;
    if (resolvedLanguage) {
      this.language = resolvedLanguage;
      if (this.session) {
        this.session.language = resolvedLanguage;
      }
    }

    const payload = {
      sequence,
      durationMs,
      mimeType,
      chunk,
    };

    if (resolvedLanguage) {
      payload.language = resolvedLanguage;
    }

    this.queue = this.queue
      .catch((error) => {
        console.warn('Vorheriger Chunk fehlgeschlagen', error);
      })
      .then(async () => {
        const response = await sendLiveChunk(sessionId, payload);
        this.#handleServerUpdate(response);
      });

    return this.queue;
  }

  async finalize() {
    if (!this.session) return null;
    this.#notifyStatus('Session wird beendet…');
    await this.queue.catch(() => {});
    const result = await stopLiveSession(this.session.sessionId);
    this.#handleServerUpdate(result);
    this.#notifyStatus('Session abgeschlossen');
    return result;
  }

  #handleServerUpdate(payload) {
    if (!payload) return;
    const hasTranscript = Boolean(payload.transcript);
    if (hasTranscript && typeof this.onTranscript === 'function') {
      this.onTranscript(payload.transcript);
    }
    const requirementItems = Array.isArray(payload?.events?.items)
      ? payload.events.items
      : Array.isArray(payload?.events)
      ? payload.events
      : Array.isArray(payload?.requirements?.items)
      ? payload.requirements.items
      : Array.isArray(payload?.requirements)
      ? payload.requirements
      : null;
    if (Array.isArray(requirementItems)) {
      this.requirements = requirementItems;
      if (typeof this.onRequirements === 'function') {
        this.onRequirements(this.requirements);
      }
    }
    if (payload.userStories?.items) {
      this.userStories = payload.userStories.items;
    }
    const hasDeferredDecisions = Array.isArray(payload.deferredDecisions)
      || Array.isArray(payload.deferredDecisions?.items);
    if (hasDeferredDecisions) {
      const deferred = Array.isArray(payload.deferredDecisions)
        ? payload.deferredDecisions
        : payload.deferredDecisions.items;
      this.deferredDecisions = deferred;
      if (typeof this.onDeferredDecisions === 'function') {
        this.onDeferredDecisions(this.deferredDecisions);
      }
    }
    if ('language' in payload) {
      this.language = payload.language ?? null;
      if (this.session) {
        this.session.language = this.language;
      }
      if (typeof this.onLanguage === 'function') {
        this.onLanguage(this.language);
      }
    }
    if (payload?.transcriptionEngine) {
      this.transcriptionEngine = payload.transcriptionEngine;
      if (this.session) {
        this.session.transcriptionEngine = this.transcriptionEngine;
      }
      if (typeof this.onEngine === 'function') {
        this.onEngine(this.transcriptionEngine);
      }
    }
    if (Array.isArray(payload?.extractionWarnings)) {
      this.extractionWarnings = payload.extractionWarnings;
      if (typeof this.onWarnings === 'function') {
        this.onWarnings(this.extractionWarnings);
      }
    }
    if (typeof this.onChunkStatus === 'function') {
      if (payload.buffered) {
        this.onChunkStatus({ buffered: true, reason: payload.reason ?? null, sequence: payload.sequence });
      } else if (payload.skipped) {
        this.onChunkStatus({ skipped: true, reason: payload.reason ?? null, sequence: payload.sequence });
      } else if (hasTranscript && payload.transcript.sequence != null) {
        this.onChunkStatus({ skipped: false, sequence: payload.transcript.sequence });
      }
    }
  }

  #notifyStatus(message) {
    if (typeof this.onStatus === 'function') {
      this.onStatus(message);
    }
  }
}
